import { create } from "zustand";
import { getAnnouncements } from "@/features/admin/api";
import type { Announcements } from "@/features/admin/types";

const POLL_MS = 60000;

interface AnnouncementsStore {
  announcements: Announcements | null;
  /** Number of mounted consumers — the poll runs while > 0. */
  subscribers: number;
  timer: ReturnType<typeof setInterval> | null;
  refresh: () => Promise<void>;
  /** Consumer mounted: bump the count and start the single shared poll. */
  acquire: () => void;
  /** Consumer unmounted: drop the count and stop polling when it hits zero. */
  release: () => void;
}

/**
 * Single source of truth for firm-facing announcements. Both the popup/banner
 * (`AnnouncementsCenter`) and the dashboard Events/Resources cards read from
 * here, so there is exactly one `setInterval` polling `/platform/announcements/`
 * no matter how many consumers mount — not one per component.
 */
export const useAnnouncementsStore = create<AnnouncementsStore>((set, get) => ({
  announcements: null,
  subscribers: 0,
  timer: null,
  refresh: async () => {
    try {
      set({ announcements: await getAnnouncements() });
    } catch {
      // Non-critical UI; a failed poll just leaves the last value in place.
    }
  },
  acquire: () => {
    const { subscribers, timer, refresh } = get();
    set({ subscribers: subscribers + 1 });
    if (timer === null) {
      void refresh();
      set({ timer: setInterval(() => void get().refresh(), POLL_MS) });
    }
  },
  release: () => {
    const { subscribers, timer } = get();
    const next = subscribers - 1;
    if (next <= 0 && timer !== null) {
      clearInterval(timer);
      set({ subscribers: 0, timer: null, announcements: null });
    } else {
      set({ subscribers: Math.max(0, next) });
    }
  },
}));
